import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { DATE_FNS_LOCALES } from '@/lib/format-locale'
import { LANGUAGE_FLAGS } from '@/lib/languages'
import i18n, {
  applyLanguage,
  buildLocalizedPath,
  isLanguage,
  persistLanguage,
  translate,
  type Language,
  type TranslateParams,
} from '@/lib/i18n'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string, params?: TranslateParams) => string
}

/**
 * Bandeiras v\u00EAm do registro de idiomas (`@/lib/languages`) \u2014 fonte \u00FAnica.
 * Re-exportado com o nome antigo para n\u00E3o tocar os consumidores existentes.
 */
const flags = LANGUAGE_FLAGS

function currentLanguage(): Language {
  return isLanguage(i18n.language) ? i18n.language : 'pt'
}

const LanguageContext = createContext<LanguageContextType | null>(null)

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(currentLanguage)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const onChanged = (lng: string) => {
      if (isLanguage(lng)) setLanguageState(lng)
    }
    i18n.on('languageChanged', onChanged)
    return () => {
      i18n.off('languageChanged', onChanged)
    }
  }, [])

  // Trocar de idioma navega para a URL equivalente (D2): a URL é a autoridade,
  // então estado e endereço nunca divergem e o link continua compartilhável.
  // Página, querystring E HASH são preservados — o hash é a posição do usuário
  // dentro da página (`#resultados`), e perdê-lo joga a leitura de volta ao
  // topo do documento só porque se trocou o idioma.
  const setLanguage = useCallback(
    (lang: Language) => {
      persistLanguage(lang)
      void applyLanguage(lang)
      const target = buildLocalizedPath(location.pathname, lang, location.search, location.hash)
      if (target !== `${location.pathname}${location.search}${location.hash}`) {
        navigate(target)
      }
    },
    [navigate, location.pathname, location.search, location.hash],
  )

  const t = useCallback(
    (key: string, params?: TranslateParams): string => translate(key, params),
    [language],
  )

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider')
  return ctx
}

// DATE_FNS_LOCALES agora mora em `@/lib/format-locale` (Tarefa 13) junto das
// demais funções de formatação por idioma; re-exportado aqui para não tocar os
// consumidores existentes (ReplyCard, TopicCard, ForumTopicPage).
export { flags, type Language, DATE_FNS_LOCALES }
